import { useCallback, useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight, faArrowRotateRight, faChartLine, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons"
import SubTitle from "../../components/template/SubTitle"
import Spinner from "../../components/template/Spinner"
import ButtonWrap from "../../components/template/ButtonWrap"
import api from "../../components/utils/api"
import { TRAINING_MODES } from "./trainingOptions"

const formatRemaining = (seconds) => {
    if (seconds == null) return null
    const minutes = Math.round(seconds / 60)
    if (minutes < 1) return "<1m left"
    if (minutes < 60) return `${minutes}m left`
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m left`
}

// per-state styling: status pill + dimmed detail chips (terminal states the
// user no longer needs to look at). The card frame itself is uniform — the
// pill alone carries the state color.
const LIVE_STATUS_STYLES = {
    pending: { pill: "bg-amber-100 text-amber-700", dim: false },
    preparing: { pill: "bg-green-100 text-green-700", dim: false },
    running: { pill: "bg-green-500 text-white", dim: false },
    canceled: { pill: "bg-gray-200 text-gray-500", dim: true },
    completed: { pill: "bg-blue-100 text-blue-700", dim: false },
    failed: { pill: "bg-red-100 text-red-700", dim: false },
    missing: { pill: "bg-gray-100 text-gray-400", dim: true },
    unknown: { pill: "bg-gray-100 text-gray-400", dim: true },
}

const LiveStatusPill = ({ styleKey, label }) => (
    <span className={`px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm whitespace-nowrap ${LIVE_STATUS_STYLES[styleKey].pill}`}>
        {label}
    </span>
)

// Trainings triggered from the webview for this experiment, with each one's
// current state (queue position, epoch progress, or verified outcome). The
// state is read on mount and on the section's refresh button — never polled,
// so the GPU server is only queried when someone asks. `onTrain` renders the
// New Training button in the section header when provided (the parent omits
// it for experiments that cannot be trained here, passing `disabledReason`
// to say why instead). `onLoaded` reports the
// trainings' result-folder names so the parent can split externally
// trained models into their own section. `notice` is shown above the cards
// (the parent sets it after triggering a training, so the confirmation
// appears where the new card shows up).
const TrainingList = ({ experimentId, refreshKey, onTrain, disabledReason, onLoaded, notice, onDismissNotice }) => {
    const [trainings, setTrainings] = useState([])
    const [isGpuQueueAvailable, setIsGpuQueueAvailable] = useState(true)
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadFailed, setIsLoadFailed] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [cancelNotice, setCancelNotice] = useState("")
    const [cancelingId, setCancelingId] = useState(null)

    const fetchTrainings = useCallback(async () => {
        try {
            const res = await api.get(`/api/experiments/${experimentId}/trainings`)
            setTrainings(res.data.trainings)
            setIsGpuQueueAvailable(res.data.gpu_queue_available)
            setIsLoadFailed(false)
            onLoaded?.(new Set(res.data.trainings.map(t => t.expected_result_folder)))
        } catch (error) {
            console.error(error)
            setIsLoadFailed(true)
            // report an empty set so the parent's models section is not stuck
            // waiting for a split that will not arrive
            onLoaded?.(new Set())
        } finally {
            setIsLoading(false)
        }
    }, [experimentId, onLoaded])

    useEffect(() => { fetchTrainings() }, [fetchTrainings, refreshKey])

    const onCancel = async (training) => {
        const consequence = training.live_status?.state === "running"
            ? "The run will be stopped immediately and its progress will be lost."
            : "It will be removed from the queue."
        const message = `Cancel the ${training.architecture} · ${training.sample_rate} training?\n\n`
            + `${consequence}\nThis cannot be undone. To train this model again, submit a new training.`
        if (!window.confirm(message)) return
        setErrorMessage("")
        setCancelNotice("")
        setCancelingId(training.id)
        const model = `${training.architecture} · ${training.sample_rate}`
        try {
            const res = await api.delete(`/api/experiments/${experimentId}/trainings/${training.id}`)
            // drop a stale submission notice so only the latest outcome shows
            onDismissNotice?.()
            setCancelNotice(res.data?.was_running
                ? `${model} training canceled. The run was stopped and the GPUs are free again.`
                : `${model} training canceled. It was removed from the queue before starting.`)
        } catch (error) {
            console.error(error)
            setErrorMessage(error.response?.data?.message || "Failed to cancel the training. Please try again.")
        } finally {
            setCancelingId(null)
        }
        fetchTrainings()
    }

    // maps a training to its display state (pill label + LIVE_STATUS_STYLES key)
    const resolveStatus = (training) => {
        const liveStatus = training.live_status
        if (!liveStatus || !LIVE_STATUS_STYLES[liveStatus.state]) return { key: "unknown", label: "unknown" }
        if (liveStatus.state === "pending") return {
            key: "pending",
            label: `queued${liveStatus.queue_position != null ? ` #${liveStatus.queue_position}` : ""}`,
        }
        return { key: liveStatus.state, label: liveStatus.state }
    }

    const isCancellable = (training) =>
        ["pending", "preparing", "running"].includes(training.live_status?.state)

    return (
        <div className="mt-8">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <SubTitle title="Trainings" />
                    <button
                        onClick={fetchTrainings}
                        title="Refresh trainings"
                        className="text-gray-400 hover:text-gray-600"
                    ><FontAwesomeIcon icon={faArrowRotateRight} /></button>
                </div>
                <div className="grid grid-flow-col gap-2 items-center">
                    <ButtonWrap
                        contents={<><FontAwesomeIcon icon={faChartLine} /> Compare Runs</>}
                        onClick={() => window.open(`/experiments/${experimentId}/comparison`, "_blank", "noopener,noreferrer")}
                    />
                    {onTrain && <ButtonWrap contents="New Training" onClick={onTrain} />}
                </div>
            </div>
            {disabledReason && <div className="mt-3 bg-gray-50 border border-gray-200 text-gray-500 text-sm rounded px-4 py-2 text-left">
                {disabledReason}
            </div>}
            {(cancelNotice || notice) && <div className="mt-3 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded px-4 py-2 flex justify-between items-center">
                <span>{cancelNotice || notice}</span>
                <button
                    onClick={() => cancelNotice ? setCancelNotice("") : onDismissNotice?.()}
                    title="Dismiss"
                    className="text-blue-400 hover:text-blue-600"
                ><FontAwesomeIcon icon={faXmark} /></button>
            </div>}
            {!isGpuQueueAvailable && <div className="my-2 text-left text-sm text-gray-400">
                The GPU server could not be reached — queue states are unknown.
            </div>}
            {errorMessage && <div className="my-2 text-left text-sm text-red-500">{errorMessage}</div>}
            {isLoading
                ? <div className="my-3"><Spinner /></div>
                : isLoadFailed
                ? <div className="my-3 text-sm text-red-500">
                    Failed to load the trainings. Use the refresh button to try again.
                </div>
                : trainings.length === 0
                ? <div className="my-3 text-sm text-gray-500">No trainings triggered yet.</div>
                : <div className="my-3 space-y-3">
                    {trainings.map(training => {
                        const status = resolveStatus(training)
                        const styles = LIVE_STATUS_STYLES[status.key]
                        const progress = training.live_status?.training_progress?.[0]
                        const remaining = formatRemaining(training.live_status?.estimated_remaining_seconds)
                        return (
                            <div key={training.id} className="border border-gray-200 rounded-xl">
                                <div className="flex justify-between items-center gap-3 px-4 py-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <LiveStatusPill styleKey={status.key} label={status.label} />
                                        <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                                            {training.architecture} · {training.sample_rate}
                                        </span>
                                        <span className="text-xs text-gray-500 border rounded-full px-2 py-0.5 whitespace-nowrap">
                                            {TRAINING_MODES[training.mode]?.label || training.mode}
                                        </span>
                                        {isCancellable(training) && <button
                                            onClick={() => onCancel(training)}
                                            disabled={cancelingId === training.id}
                                            title={cancelingId === training.id ? "Canceling..." : "Cancel training"}
                                            className="text-gray-400 hover:text-red-500 disabled:opacity-40 disabled:hover:text-gray-400"
                                        >{cancelingId === training.id
                                            ? <FontAwesomeIcon icon={faSpinner} spin />
                                            : <FontAwesomeIcon icon={faXmark} />}</button>}
                                    </div>
                                    <div className="flex items-center gap-6">
                                        {status.key === "running" && progress && <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                                            {progress.total_epochs && <span className="font-semibold">
                                                Epoch {progress.current_epoch}/{progress.total_epochs}
                                            </span>}
                                            {progress.total_epochs && remaining && <span className="text-gray-300">•</span>}
                                            {remaining && <span>{remaining}</span>}
                                        </div>}
                                        {/* the chain downloads its training data first, so there is no
                                            result folder (and no report) to link to during that stage */}
                                        {status.key === "preparing" && <span className="flex items-center gap-2 text-sm text-gray-400 whitespace-nowrap animate-pulse">
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                            Preparing training data
                                        </span>}
                                        {(status.key === "completed" || status.key === "failed" || (status.key === "running" && progress)) && <a
                                            href={`/training-progress/models/${encodeURIComponent(training.expected_result_folder)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={{
                                                running: "Show live training report",
                                                completed: "Show training report",
                                                failed: "Show partial training report",
                                            }[status.key]}
                                            className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-gray-600 whitespace-nowrap"
                                        >Show report <FontAwesomeIcon icon={faArrowRight} /></a>}
                                    </div>
                                </div>
                                <div className="border-t px-4 py-2 flex justify-between items-center gap-3">
                                    <span className={`text-xs font-mono border rounded px-2 py-0.5 truncate ${styles.dim
                                        ? "bg-gray-50 border-gray-100 text-gray-300"
                                        : "bg-gray-50 border-gray-200 text-gray-600"}`}>
                                        {training.expected_result_folder}
                                    </span>
                                    <div className={`text-sm flex items-center gap-2 whitespace-nowrap ${styles.dim ? "text-gray-300" : "text-gray-400"}`}>
                                        <span>{training.created_at}</span>
                                        <span>•</span>
                                        <span>{training.requested_by}</span>
                                        {training.mode === "distill" && <>
                                            <span>•</span>
                                            <span className="text-xs bg-blue-50 text-blue-600 rounded px-1.5 py-0.5" title={training.teachers.join("\n")}>
                                                {training.teachers.length} teacher(s)
                                            </span>
                                        </>}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>}
        </div>
    )
}

export default TrainingList
